import { isCommentPinned } from '@helpers/bitHelper.js'
import { KEYS_DISCUSSION } from '@config/storeKeys.js'
import { clone } from '@helpers/utils.js'
import { store } from '@stores'
import { computed } from 'vue'

const { getStore, updateStore } = store()

export function recurseDiscussionCount(discussion) {
  let total = discussion?.replies?.length || 0
  if (discussion.replies) {
    discussion.replies.forEach((r) => {
      total += recurseDiscussionCount(r)
    })
  }

  return total
}

export default function (type) {
  function generateId(gameNameId, resourceNameId) {
    return `${KEYS_DISCUSSION}${type}${gameNameId}${resourceNameId}`
  }
  if (!type) {
    throw new Error('discussionStore requires type param')
  }

  function getComputedDiscussionPage(gameNameId, resourceNameId) {
    const discussions = getStore(generateId(gameNameId, resourceNameId))

    return computed(() => ({
      total: discussions.value?.result_total || 0,
      offset: discussions.value?.result_offset || 0,
      count: discussions.value?.result_count || 0,
    }))
  }

  function getComputedDiscussionsTotal(gameNameId, resourceNameId) {
    const discussions = getStore(generateId(gameNameId, resourceNameId))
    return computed(() => discussions.value?.result_total || 0)
  }

  function addDiscussions(gameNameId, resourceNameId, apiResult) {
    if (apiResult.data?.length) {
      updateStore(generateId(gameNameId, resourceNameId), {
        ...apiResult,
        ...mergeDiscussions(apiResult.data),
      })
    }
  }

  function getDiscussions(gameNameId, resourceNameId, apiResult = false) {
    const discussion = getStore(generateId(gameNameId, resourceNameId))
    return computed(() => {
      return discussion.value?.data?.length > 0
        ? apiResult
          ? discussion.value
          : discussion.value.data
        : null
    })
  }

  function addDiscussion(gameNameId, resourceNameId, discussionData) {
    const discussions = getDiscussions(gameNameId, resourceNameId, true)

    if (discussions.value) {
      const _discussions = clone(discussions.value)
      _discussions.result_total += 1
      _discussions.result_count += 1

      const data = mergeDiscussions(
        [discussionData],
        _discussions.dataObj,
        false
      )

      updateStore(generateId(gameNameId, resourceNameId), {
        ..._discussions,
        ...data,
      })
    } else {
      updateStore(generateId(gameNameId, resourceNameId), {
        ...mergeDiscussions([discussionData]),
        result_total: 1,
        result_count: 1,
      })
    }
  }

  function updateDiscussion(gameNameId, resourceNameId, commentId, update) {
    const discussions = getDiscussions(gameNameId, resourceNameId, true)
    if (discussions.value) {
      const _discussions = clone(discussions.value)
      const comment = _discussions.dataObj[commentId]

      // Need to update the comment in-place, not creating a new object.
      // This keeps the parent/child relationship in the stored data.
      for (const key in update) comment[key] = update[key]

      updateStore(generateId(gameNameId, resourceNameId), {
        ..._discussions,
        ...mergeDiscussions([], _discussions.dataObj),
      })
    }
  }

  function appendDiscussions(gameNameId, resourceNameId, newDiscussions) {
    const discussions = getDiscussions(gameNameId, resourceNameId, true)
    updateStore(generateId(gameNameId, resourceNameId), {
      ...newDiscussions,
      ...mergeDiscussions(newDiscussions.data, discussions.value.dataObj),
    })
  }

  function removeDiscussion(gameNameId, resourceNameId, commentId) {
    const discussions = getDiscussions(gameNameId, resourceNameId, true)
    if (discussions.value) {
      const _discussions = clone(discussions.value)
      const comment = _discussions.dataObj[commentId]
      if (comment?.reply_id >= 0) {
        _discussions.dataObj[comment.reply_id].replies = _discussions.dataObj[
          comment.reply_id
        ].replies.filter((c) => c.id !== commentId)
      }

      if (comment) {
        const removedCount = recurseDiscussionCount(comment) + 1
        _discussions.result_total -= removedCount
        _discussions.result_count -= removedCount
        delete _discussions.dataObj[commentId]
      }

      updateStore(generateId(gameNameId, resourceNameId), {
        ..._discussions,
        ...mergeDiscussions([], _discussions.dataObj),
      })
    }
  }

  function getTopLevelComment(gameNameId, resourceNameId, commentId) {
    const dataObj = getDiscussions(gameNameId, resourceNameId, true).value
      .dataObj

    let result = dataObj[commentId]

    while (result?.reply_id) {
      const comment = dataObj[result.reply_id]

      if (comment) result = comment
      else break
    }

    return result
  }

  function mergeDiscussions(discussions, storedComments = {}, append = true) {
    const _storedComments = clone(storedComments)

    for (const comment of discussions) {
      comment.replies ||= []

      // Need to check thread_position because existing discussions may have a
      // comment not yet fetched.
      if (comment.thread_position) {
        const positionParts = comment.thread_position.split('.')
        comment.level = positionParts.length
        comment.position = positionParts.pop()
      }

      if (_storedComments[comment.id])
        comment.replies = _storedComments[comment.id].replies

      _storedComments[comment.id] = comment

      const parent = _storedComments[comment.reply_id]
      if (parent) {
        if (append) parent.replies.push(comment)
        else parent.replies.unshift(comment)
      } else {
        _storedComments[comment.reply_id] = {
          id: comment.reply_id,
          replies: [comment],
        }
      }
    }

    const data = _storedComments[0].replies
    data.sort((a, b) => {
      const aPinned = isCommentPinned(a.options)
      const bPinned = isCommentPinned(b.options)
      if (aPinned === bPinned) {
        return b.date_added - a.date_added
      }

      return aPinned ? -1 : 1
    })

    return {
      dataObj: _storedComments,
      data,
    }
  }

  return {
    getComputedDiscussionsTotal,
    getComputedDiscussionPage,
    getTopLevelComment,
    appendDiscussions,
    updateDiscussion,
    removeDiscussion,
    addDiscussions,
    getDiscussions,
    addDiscussion,
  }
}
